/* eslint-disable */
import axios from 'axios';
import { ElMessageBox } from 'element-plus';
import { getToken, setToken, removeToken } from '@/utils/auth';

// Types d'erreurs (copie locale pour éviter import cyclique)
const ErrorTypes = {
  NETWORK: 'NETWORK',
  VALIDATION: 'VALIDATION',
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORIZATION: 'AUTHORIZATION',
  NOT_FOUND: 'NOT_FOUND',
  SERVER: 'SERVER',
  CLIENT: 'CLIENT',
  UNKNOWN: 'UNKNOWN'
};

// Create axios instance
const service = axios.create({
  baseURL: '',
  timeout: 10000,
});

// Request interceptor
service.interceptors.request.use(
  config => {
    // Timeout spécifique pour la requête
    if (config.override_timeout) {
      config.timeout = config.override_timeout;
    }

    // ResponseType spécifique (utile pour des requêtes PDF)
    if (config.override_responseType) {
      config.responseType = config.override_responseType;
    }

    const token = getToken();
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor
service.interceptors.response.use(
  response => {
    // Rafraîchir le token si présent dans la réponse
    if (response.headers.authorization) {
      setToken(response.headers.authorization);
      response.data.token = response.headers.authorization;
    }

    return response.data;
  },
  error => {
    // Cas 1 : aucune réponse serveur (network, timeout, CORS, etc.)
    if (!error.response) {
      const enrichedError = new Error(error.message || 'Erreur réseau');
      enrichedError.isNetworkError = true;
      enrichedError.type = ErrorTypes.NETWORK;
      enrichedError.originalError = error;
      return Promise.reject(enrichedError);
    }

    // À partir d'ici, error.response existe
    const status = error.response.status;
    const data = error.response.data || {};

    // Scrub sécurité : pour les routes qui transportent un secret (clé SSH,
    // token, mdp), on redacte `config.data` AVANT que l'erreur soit propagée
    // aux catches/Sentry/devtools. Le secret a déjà été envoyé au serveur ;
    // il n'a plus rien à faire dans le canal d'erreur côté client.
    const reqUrl = error.response?.config?.url || '';
    if (/\/api\/provisioning\/secrets/.test(reqUrl) && error.response?.config?.data) {
      error.response.config.data = '[REDACTED]';
    }

    // Enrichir l'erreur avec des informations utiles
    const enrichedError = new Error(error.message);
    enrichedError.response = error.response;
    enrichedError.status = status;
    enrichedError.originalError = error;

    // Déterminer le type d'erreur et le message
    switch (status) {
      case 400:
        enrichedError.type = ErrorTypes.VALIDATION;
        enrichedError.message = data.message || 'Requête invalide';
        break;

      case 401:
        enrichedError.type = ErrorTypes.AUTHENTICATION;
        if (data.message === 'Unauthenticated.') {
          removeToken();
          ElMessageBox.alert(
            'Vous allez être redirigé vers la page de connexion',
            'Session expirée',
            {
              confirmButtonText: 'OK',
              callback: () => {
                document.location.assign(window.location.pathname);
              }
            }
          );
        } else if (data.type === 'login') {
          enrichedError.message = 'Identifiant ou mot de passe incorrect';
        } else {
          enrichedError.message = data.message || 'Non authentifié';
        }
        break;

      case 403:
        enrichedError.type = ErrorTypes.AUTHORIZATION;
        enrichedError.message = data.message || 'Accès refusé';
        break;

      case 404:
        enrichedError.type = ErrorTypes.NOT_FOUND;
        enrichedError.message = data.message || 'Ressource non trouvée';
        break;

      case 422:
        enrichedError.type = ErrorTypes.VALIDATION;
        enrichedError.message = data.message || 'Vérifiez les champs';
        enrichedError.validationErrors = data.errors || null;
        break;

      case 500:
      case 502:
      case 503:
      case 504:
        enrichedError.type = ErrorTypes.SERVER;
        enrichedError.message = 'Erreur serveur. Réessayez plus tard.';
        break;

      default:
        enrichedError.type = ErrorTypes.UNKNOWN;
        enrichedError.message = data.message || data.error || error.message;
    }

    return Promise.reject(enrichedError);
  }
);

export default service;